// src/components/forms/validators.ts
import { v4 as uuidv4 } from 'uuid';
import { TableField } from "../types/interfaces";

// Typesafe result
export type FieldValidationResult = {
    valid: boolean;
    message?: string;
};

export const validateFieldValue = (field: TableField, value: string | null | undefined): FieldValidationResult => {
    if (!field) {
        return { valid: false, message: "Invalid field definition." };
    }

    if (value===undefined || value == '')
    {
        if (field.required) {
            return { valid: false, message: `'${field.fieldName}' is required.` };
        }
      
    }
    const trimmed = (value ?? "").trim();

    // Required check
    if (field.required && trimmed === "") {
        return { valid: false, message: `'${field.fieldName}' is required.` };
    }

    // Basic injection prevention
    if (
        field.allowSpecialCharacters === false &&
        (trimmed.toLowerCase().includes("<script") || trimmed.toLowerCase().includes("drop table"))
    ) {
        return { valid: false, message: `'${field.fieldName}' contains potentially dangerous input.` };
    }

    // Length
    if (field.length && trimmed.length > field.length) {
        return { valid: false, message: `'${field.fieldName}' exceeds max length of ${field.length}.` };
    }

    // Type-specific checks
    switch ((field.dataType || "").toLowerCase()) {
        case "int":
        case "integer":
            if (!/^-?\d+$/.test(trimmed)) return { valid: false, message: `'${field.fieldName}' must be an integer.` };
            break;

        case "decimal": 
            if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return { valid: false, message: `'${field.fieldName}' must be a decimal.` };
            break;

        case "boolean":
        case "bool":
            if (!["true", "false", "1", "0", ''].includes(trimmed.toLowerCase())) {
                return { valid: false, message: `'${field.fieldName}' must be true or false.` };
            }
            break;

        case "date":
        case "datetime":
            if (trimmed.length>0 && isNaN(Date.parse(trimmed))) {
                return { valid: false, message: `'${field.fieldName}' must be a valid date.` };
            }
            break;

        case "email":
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
                return { valid: false, message: `'${field.fieldName}' must be a valid email address.` };
            }
            break;
    }

    // Regex InputMask
    if (field.inputMask) {
        try {
            const mask = new RegExp(field.inputMask);
            if (!mask.test(trimmed)) {
                return { valid: false, message: `'${field.fieldName}' does not match required format.` };
            }
        } catch (err) {
            console.warn("Invalid inputMask regex for field", field.fieldName, err);
        }
    }

    // Min/Max (if numeric)
    if (field.min!=null || field.max!=null ) {
        const asNumber = parseFloat(trimmed);
        if (!isNaN(asNumber)) {
            if (field.min !== undefined && asNumber < field.min) {
                return { valid: false, message: `'${field.fieldName}' must be ≥ ${field.min}` };
            }
            if (field.max !== undefined && asNumber > field.max) {
                return { valid: false, message: `'${field.fieldName}' must be ≤ ${field.max}` };
            }
        }
    }

    return { valid: true };
};

export function validateTableFields(fields: TableField[], values: Record<string, string>): FieldValidationResult[] {
    return fields.map(field => {
        const value = values[field.fieldName]; // Or `fieldID`, depending on your form structure
        return validateFieldValue(field, value);
    }).filter(result => !result.valid);
}


// Use this to enforce clean naming
export const isValidInputName = (value: string): boolean => {
    return /^[a-zA-Z0-9_\-\. ]+$/.test(value); // Accepts alphanum, underscore, hyphen, dot, space
};

// 🔐 Basic sanitization for values
export const sanitizeText = (value: string): string => {
    return value.replace(/[<>'"]/g, "").trim();
};


export function customFormatDate(value: any, format?: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    // Simplified for now. Extend using date-fns or dayjs if needed.
    return format === "MM/DD/YYYY"
        ? `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
        : date.toLocaleDateString();
}

export function createUUID(): string {
    return uuidv4();

    //return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    //    const r = Math.random() * 16 | 0;
    //    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    //    return v.toString(16);
    //  });
}